<script setup lang="ts">
import { computed } from 'vue'
import type { AgentRepoStatus } from '../stores/agent_repos'

// Declared vs. published host ports (#1262) — a section of the status grid in
// AdminAgentRepos.vue. Renders as bare `dt`/`dd` pairs (a Vue 3 multi-root
// component) so it slots into the parent's `<dl>` without a wrapper, same as
// AgentRepoCheckout.
//
// The two readings are deliberately separate. `ports` is what an admin typed
// on the onboarding form, and it is what the overlap check had to go on;
// `published_ports` is what the compose files on disk actually publish. They
// drifted apart unnoticed until five of the agent box's seven real host-port
// allocations were invisible to the check meant to police them — so the
// disagreement between them is the thing worth putting on the page.
const props = defineProps<{ repo: AgentRepoStatus; allRepos: AgentRepoStatus[] }>()

const declared = computed(() => props.repo.ports)
const published = computed(() => props.repo.published_ports)

// Ports the checkout publishes that nothing declared. A null `published_ports`
// means the compose files couldn't be read, which is not the same as "nothing
// undeclared" — so it contributes nothing here rather than reading as clean.
const undeclared = computed(() => (published.value ?? []).filter((port) => !declared.value.includes(port)))

// Nothing to say about a repo that neither declares nor publishes anything —
// most onboarded repos publish no host port at all, and an empty row for every
// one of them would bury the repos where this matters.
const hasAnything = computed(() => declared.value.length > 0 || (published.value?.length ?? 0) > 0)

const declaredLabel = computed(() => (declared.value.length > 0 ? declared.value.join(', ') : 'None declared'))
const publishedLabel = computed(() => {
  if (published.value === null) return 'Unknown'
  return published.value.length > 0 ? published.value.join(', ') : 'None'
})

// Same effective-allocation rule the backend overlap check uses (#1288):
// declared UNION published. A repo can collide purely on its published side —
// the DB never validated it and there's no form field to fix it from — so
// this has to read both, not just `declared`.
function effectivePorts(r: AgentRepoStatus): number[] {
  return [...new Set([...r.ports, ...(r.published_ports ?? [])])]
}

// A pre-existing collision no longer blocks saving an unrelated field
// (#1288) — the save-time check only fires when THIS repo's declared ports
// change. Surfaced here instead, so a real overlap stays visible rather than
// silently going unenforced.
const conflict = computed(() => {
  const mine = effectivePorts(props.repo)
  if (mine.length === 0) return null

  for (const other of props.allRepos) {
    if (other.slug === props.repo.slug) continue
    const shared = mine.filter((port) => effectivePorts(other).includes(port))
    if (shared.length > 0) return { slug: other.slug, ports: shared }
  }
  return null
})
</script>

<template>
  <template v-if="hasAnything">
    <dt class="text-ink-faint">Ports</dt>
    <dd
      class="m-0 font-mono tabular-nums"
      :class="declared.length ? 'text-ink-muted' : 'text-ink-faint'"
      :data-testid="`agent-repo-ports-${repo.slug}`"
    >
      {{ declaredLabel }}
    </dd>
    <dt class="text-ink-faint">Published</dt>
    <dd
      class="m-0 font-mono tabular-nums"
      :class="undeclared.length ? 'text-factory' : 'text-ink-muted'"
      :data-testid="`agent-repo-published-ports-${repo.slug}`"
    >
      {{ publishedLabel }}
      <span v-if="undeclared.length" class="text-ink-faint">({{ undeclared.join(', ') }} undeclared)</span>
    </dd>
    <template v-if="conflict">
      <dt class="text-factory">Conflict</dt>
      <dd class="m-0 text-factory" :data-testid="`agent-repo-port-conflict-${repo.slug}`">
        {{ conflict.ports.join(', ') }} also claimed by {{ conflict.slug }}
      </dd>
    </template>
  </template>
</template>
